import { FC, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { InfoOutlined } from '@mui/icons-material';
import { ChartType } from '@constants/constants';
import { useLazyDownloadChartCsvQuery, useLazyGetChartByIdQuery } from '@api/charts';
import BarChart from './components/BarChart';
import MultiLineChart from './components/MultiLineChart';
import BarLineChart from './components/BarLineChart';
import {
  ChartSeries,
  BoxPlotChartSeries,
  TypedChartData,
  UnderwritingChartMetadata,
  UnderwritingChartsApiResponse,
  BubbleChartSeries,
} from './types';
import { ChartLoading } from './components/ChartLoading';
import { ChartError } from './components/ChartError';
import StackedBarChart from './components/StackedBarChart';
import BoxPlotChart from './components/BoxPlotChart';
import BubbleChart from './components/BubbleChart';
import Table from './components/Table';

interface ChartLayoutProps {
  filters: { dates: string[]; gameId: string };
  chartTitle: string;
  showDefinitionSidePanel: () => void;
  chartType: ChartType;
  submoduleId: string;
  chartId: string;
  renderForExport?: boolean;
  setDefinitions?: (definitions: { label: string; description: string }[]) => void;
}

/* Helper functions */
const getYAxisLabelFromChartMetadata = (metadata: UnderwritingChartMetadata): string => {
  if (metadata.yAxisLabel) return metadata.yAxisLabel;
  return 'Y axis label';
};

const getXAxisLabelFromChartMetadata = (metadata: UnderwritingChartMetadata): string => {
  if (metadata.xAxisLabel) return metadata.xAxisLabel;
  return 'X axis label';
};

const ChartLayout: FC<ChartLayoutProps> = ({
  filters,
  chartTitle,
  chartType,
  submoduleId,
  chartId,
  renderForExport = true,
  showDefinitionSidePanel,
  setDefinitions,
}) => {
  const [getChartById, { isLoading: isChartLoading }] = useLazyGetChartByIdQuery();
  const [chartResponse, setChartResponse] = useState<UnderwritingChartsApiResponse | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartMetadata, setChartMetadata] = useState<UnderwritingChartMetadata | null>(null);
  // APIs
  const [downloadChartCsv] = useLazyDownloadChartCsvQuery();

  useEffect(() => {
    if (submoduleId && chartId && filters.gameId) {
      fetchChartData();
    }
  }, [submoduleId, chartId, filters]);

  const constructQueryParams = (): string => {
    const qps: string[] = [];

    if (filters.gameId) {
      qps.push(`gameId=${filters.gameId}`);
    }

    if (filters.dates) {
      qps.push(`cohort=${filters.dates}`);
    }

    return qps.length > 0 ? `?${qps.join('&')}` : '';
  };

  const fetchChartData = async () => {
    try {
      setChartError(null);
      const queryParams = constructQueryParams();

      const chartApiResponse: UnderwritingChartsApiResponse = await getChartById({
        submoduleId: submoduleId,
        chartId: chartId,
        queryParams,
      }).unwrap();

      setChartResponse(chartApiResponse);
      try {
        const chartMetadata: UnderwritingChartMetadata = JSON.parse(
          chartApiResponse.data.metadata || '{}',
        );
        setChartMetadata(chartMetadata);
      } catch (error) {
        console.error('Error parsing chart metadata');
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setChartError('Failed to load chart data');
    }
  };

  const renderChartComponent = () => {
    if (isChartLoading) {
      return <ChartLoading chartType={chartType} />;
    }

    if (chartError) {
      return <ChartError error={chartError} />;
    }

    // TODO: Consider different empty cases for Line, Bar, Bar-line.
    if (!chartResponse || chartResponse.data.result.data.length === 0) {
      return <ChartError error={'No chart data found'} />;
    }

    try {
      const chartMetadata: UnderwritingChartMetadata = JSON.parse(
        chartResponse.data.metadata || '{}',
      );
      const chartType = chartMetadata.chartType;

      const yAxisLabel = getYAxisLabelFromChartMetadata(chartMetadata);
      const xAxisLabel = getXAxisLabelFromChartMetadata(chartMetadata);
      const xAxisTransformer = chartMetadata.xAxisTransform;
      const yAxisTransformer = chartMetadata.yAxisTransform;
      const y1AxisLabel = chartMetadata.y1AxisLabel;
      const y1AxisTransformer = chartMetadata.y1AxisTransform;
      const threshold = Number(chartMetadata.threshold);
      const thresholdLabel = chartMetadata.thresholdLabel || 'Threshold';
      const tooltipConfigs = chartMetadata.tooltipConfigs;

      switch (chartType) {
        /* Multi Line Chart */
        case ChartType.MULTI_LINE_CHART:
          return (
            <MultiLineChart
              data={chartResponse.data.result.data as any[]}
              chartId={chartId}
              yAxisLabel={yAxisLabel}
              xAxisLabel={xAxisLabel}
              xAxisTransformer={xAxisTransformer}
              yAxisTransformer={yAxisTransformer}
              threshold={threshold}
              thresholdLabel={thresholdLabel}
              renderForExport={renderForExport}
            />
          );

        /* Bar Chart */
        case ChartType.BAR_CHART:
          return (
            <BarChart
              data={chartResponse.data.result.data as ChartSeries[]}
              chartId={chartId}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              xAxisTransformer={xAxisTransformer}
              yAxisTransformer={yAxisTransformer}
              threshold={threshold}
              thresholdLabel={thresholdLabel}
              renderForExport={renderForExport}
            />
          );

        /* Line Bar Chart */
        case ChartType.LINE_BAR_CHART:
          return (
            <BarLineChart
              data={chartResponse.data.result.data as TypedChartData[]}
              chartId={chartId}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              yAxisTransformer={yAxisTransformer}
              xAxisTransformer={xAxisTransformer}
              y1AxisLabel={y1AxisLabel}
              y1AxisTransformer={y1AxisTransformer}
              renderForExport={renderForExport}
            />
          );

        /* Stacked Bar Chart */
        case ChartType.STACKED_BAR_CHART:
          return (
            <StackedBarChart
              data={chartResponse.data.result.data as ChartSeries[]}
              chartId={chartId}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              xAxisTransformer={xAxisTransformer}
              yAxisTransformer={yAxisTransformer}
              renderForExport={renderForExport}
            />
          );

        /* Box Plot Chart */
        case ChartType.BOX_PLOT_CHART:
          return (
            <BoxPlotChart
              data={chartResponse.data.result.data as BoxPlotChartSeries[]}
              chartId={chartId}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              xAxisTransformer={xAxisTransformer}
              yAxisTransformer={yAxisTransformer}
              tooltipConfigs={tooltipConfigs}
              renderForExport={renderForExport}
            />
          );

        /* Bubble Chart */
        case ChartType.BUBBLE_CHART:
          return (
            <BubbleChart
              data={chartResponse.data.result.data as BubbleChartSeries[]}
              chartId={chartId}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              xAxisTransformer={xAxisTransformer}
              yAxisTransformer={yAxisTransformer}
              renderForExport={renderForExport}
            />
          );

        case ChartType.TABLE:
          return (
            <Table
              data={chartResponse.data.result.data as (string | number)[][]}
              columns={chartResponse.data.result.columns as string[]}
            />
          );
        default:
          return <ChartError error={`Unsupported chart type ${chartType}`} />;
      }
    } catch (error) {
      console.error('Error parsing chart metadata');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadChartCsv({
        submoduleId: submoduleId,
        chartId: chartId,
        queryParams: constructQueryParams(),
      }).unwrap();

      const url = response.data.url;

      if (url) {
        const link = document.createElement('a');
        link.href = response.data.url || '';
        link.download = 'chart_data.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading chart data:', error);
    }
  };

  return (
    <Stack
      gap={'1rem'}
      sx={{
        padding: '1rem',
        borderRadius: '0.75rem',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        gap={'0.5rem'}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'0.5rem'}>
          {/* Chart Title */}
          <Typography fontSize={'1rem'} lineHeight={'1.5rem'} fontWeight={'700'} color={'#162936'}>
            {chartTitle}
          </Typography>

          {/* Info Button: Opens the definition side panel */}
          <IconButton
            disableFocusRipple
            disableRipple
            disableTouchRipple
            sx={{
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={() => {
              showDefinitionSidePanel();
              setDefinitions?.(chartMetadata?.definitions || []);
            }}
          >
            <InfoOutlined
              style={{
                height: '1rem',
                color: '#2F736E',
                padding: 0,
              }}
            />
          </IconButton>
        </Stack>

        {/* Download Button */}
        <IconButton
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={handleDownload}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
      </Stack>

      <Box sx={{ height: '100%', width: '100%' }}>{renderChartComponent()}</Box>
    </Stack>
  );
};

export default ChartLayout;
